import { useCallback, useEffect, useRef, useState } from 'react';
import { withNoSSR } from '@/components/NoSSR';
import { useBreakPointDown } from '@/hooks/breakPoint';
import { classnames } from '@/utils/classnames';
import { HorizontalPaddingLayout } from './HorizontalPadding';

const STICKY_FOOTER_BOX_SHADOW = '0px -4px 20px rgba(0, 0, 0, 0.05)';

interface IStickyFooterProps
  extends React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  > {
  isStickyOnDesktop?: boolean;
  shouldShowEmptySpace?: boolean;
  isTransparent?: boolean;
}

export const StickyFooter: React.FC<
  React.PropsWithChildren<IStickyFooterProps>
> = withNoSSR(
  ({
    className,
    children,
    isStickyOnDesktop = false,
    shouldShowEmptySpace = true,
    isTransparent = false,
  }) => {
    const stickyFooterRef = useRef<HTMLDivElement>(null);
    const emptySpaceRef = useRef<HTMLDivElement>(null);
    const mainElementSelector = document.querySelector('#__main');

    const [hasScroll, setHasScroll] = useState<Boolean>(false);

    const isMdDown = useBreakPointDown('md');

    const handleHasScroll = useCallback(() => {
      if (typeof window !== 'undefined' && mainElementSelector) {
        if (mainElementSelector.scrollHeight > window.innerHeight) {
          setHasScroll(true);
        } else {
          setHasScroll(false);
        }
      }
    }, [mainElementSelector]);

    useEffect(() => {
      setTimeout(handleHasScroll, 0);
    }, [handleHasScroll]);

    const setEmptySpaceHeight = useCallback(() => {
      if (
        stickyFooterRef?.current &&
        emptySpaceRef?.current &&
        (isMdDown || isStickyOnDesktop)
      ) {
        const stickyFooterHeight = stickyFooterRef.current.clientHeight;
        if (!isStickyOnDesktop) {
          emptySpaceRef.current.style.height = `${stickyFooterHeight}px`;
        } else {
          const mainContentChildren = document.getElementById(
            'mainContentChildren'
          );
          if (mainContentChildren) {
            mainContentChildren.style.marginBottom = `${stickyFooterHeight}px`;
          }
        }
      } else if (stickyFooterRef?.current && (isMdDown || isStickyOnDesktop)) {
      }
    }, [isMdDown, isStickyOnDesktop]);

    useEffect(() => {
      setEmptySpaceHeight();
    }, [setEmptySpaceHeight]);

    const setBoxShadow = useCallback(() => {
      if (
        stickyFooterRef.current &&
        hasScroll &&
        isMdDown &&
        mainElementSelector
      ) {
        if (
          window.innerHeight + mainElementSelector.scrollTop >=
          mainElementSelector.scrollHeight - 5
        ) {
          stickyFooterRef.current.style.boxShadow = 'none';
        } else {
          stickyFooterRef.current.style.boxShadow = STICKY_FOOTER_BOX_SHADOW;
        }
      }
    }, [hasScroll, isMdDown, mainElementSelector]);

    useEffect(() => {
      if (mainElementSelector) {
        mainElementSelector.addEventListener('scroll', setBoxShadow);
        return () => {
          mainElementSelector.removeEventListener('scroll', setBoxShadow);
        };
      }
    }, [setBoxShadow, mainElementSelector]);

    return (
      <>
        {shouldShowEmptySpace && (
          <div ref={emptySpaceRef} className="md:hidden" />
        )}

        <div
          ref={stickyFooterRef}
          className={classnames(
            'fixed bottom-0 left-0 z-50 w-full py-4',
            isStickyOnDesktop
              ? 'rounded-b-xl md:absolute'
              : 'md:static md:pb-0',
            isTransparent ? 'bg-transparent' : 'bg-white',
            className
          )}
          style={{
            boxShadow:
              hasScroll && isMdDown && !isTransparent
                ? STICKY_FOOTER_BOX_SHADOW
                : 'none',
          }}
        >
          {isMdDown || isStickyOnDesktop ? (
            <HorizontalPaddingLayout>{children}</HorizontalPaddingLayout>
          ) : (
            children
          )}
        </div>
      </>
    );
  }
);
